import { useEffect, useRef, useState } from "react";
import { Canceler } from "axios";
import { useAppTable, useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import { VStack, HStack, useToast, useDisclosure, Box } from "@chakra-ui/react";
import { updatePageIndex } from "ka-table/actionCreators";
import { AppSearch } from "../../../containers";
import {
    AppAlertDelete,
    AppFilterButton,
    AppPageContent,
    AppPageHeader,
} from "../../../components";
import { CoordinatorRequestList as ListComponent } from "./CoordinatorRequestList";
import { CoordinatorRequestFilter as FilterComponent } from "./CoordinatorRequestFilter";
import {
    CoordinatorRequest as Entity,
    CoordinatorRequestApi as EntityApi,
} from "../../../apis";
import { APPC } from "../../../config";

export const CoordinatorRequestPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const deleteAlertState = useDisclosure();
    const { grant } = useAuthData();

    // state & const
    const [search, setSearch] = useState("");
    const [filterData, setFilterData] = useState<any>({});
    const [loadingList, setLoadingList] = useState(true);
    const [list, setList] = useState<Entity[]>([]);
    const [actionSelectId, setActionSelectId] = useState(0);

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const {
        table,
        pageIndex,
        pagesCount,
        pageSize,
        sortColumn,
        sortOrder,
        setPagesCount,
    } = useAppTable("createdAt", "desc");

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    const fetchListData = () => {
        if (!grant.isSuperAdmin) {
            setLoadingList(false);
            return false;
        }
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
            ...filterData,
        };
        params[`order[${sortColumn}]`] = sortOrder;

        if (search) {
            params["coordinator_request_search"] = search;
        }

        EntityApi.getCollection<Entity>(pageIndex + 1, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        })
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    setList(response.items);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const handleCreateCoordinator = (id: number) => {
        EntityApi.postCoordinatorCreate<
            {
                success: boolean;
            },
            {
                selfId: number;
            }
        >({ selfId: id }).then(({ response, errorMessage }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                if (response.success) {
                    toast({
                        title: t(
                            "padm.CoordinatorRequestPage:toast.success.createCoordinator"
                        ),
                        status: "success",
                    });
                } else {
                    toast({
                        title: t(
                            "padm.CoordinatorRequestPage:toast.fail.createCoordinator"
                        ),
                        status: "success",
                    });
                }
                fetchListData();
            }
        });
    };

    const actionClickHandler = (key: string, data?: Entity) => {
        if (!data) {
            return;
        }
        switch (key) {
            case "create":
                handleCreateCoordinator(data?.id);
                break;
            case "delete":
                setActionSelectId(data?.id);
                deleteAlertState.onOpen();
                break;
        }
    };

    const deleteHandler = async () => {
        if (actionSelectId < 1) {
            return;
        }
        setLoadingList(true);
        await EntityApi.deleteItem(actionSelectId)
            .then(({ errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else {
                    fetchListData();
                }
            })
            .finally(() => setLoadingList(false));
    };

    // useEffect
    useEffect(() => {
        fetchListData();
    }, [pageIndex, pageSize, sortColumn, sortOrder, search, filterData]);

    useEffect(() => {
        if (pageIndex > 0) {
            table.dispatch(updatePageIndex(0));
        }
    }, [filterData]);

    return (
        <>
            <AppPageHeader
                title={t("padm.CoordinatorRequestPage:text.pageTitle")}
            >
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <AppSearch
                        onSearch={(value) => {
                            setSearch(value);
                            if (pageIndex > 0) {
                                table.dispatch(updatePageIndex(0));
                            }
                        }}
                    />
                    <FilterComponent
                        filterData={filterData}
                        setFilterData={setFilterData}
                    >
                        <Box role="button">
                            <AppFilterButton filterData={filterData} />
                        </Box>
                    </FilterComponent>
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <VStack p={6}>
                    <ListComponent
                        table={table}
                        paging={paging}
                        list={list}
                        loadingList={loadingList}
                        onActionClick={actionClickHandler}
                    />
                    {deleteAlertState.isOpen && (
                        <AppAlertDelete
                            isOpenDelete={deleteAlertState.isOpen}
                            deleteHandler={() => deleteHandler()}
                            onCloseDelete={deleteAlertState.onClose}
                        />
                    )}
                </VStack>
            </AppPageContent>
        </>
    );
};
